// Copyright (c) 2024 - Present. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted

/**
 * Customer comment control component with inline display and edit capability.
 * Follows the same pattern as project descriptions with EditablePropertyButton and PopoverForm.
 */
import { EditablePropertyButton } from '@/components/editable-property-button';
import { PopoverForm } from '@/components/popover-form';
import { SupportSnackbar } from '@/components/support-snackbar';
import { Customer } from '@/customer/api/types';
import {
  UpdateCustomerParameters,
  updateCustomer,
} from '@/customer/api/update-customer';
import { useDisclosure } from '@/hooks/use-disclosure';
import { usePopoverAutoFocus } from '@/hooks/use-popover-auto-focus';
import { useTimeout } from '@/hooks/use-timeout';
import { zodResolver } from '@hookform/resolvers/zod';
import { Box, TextField, Typography, useTheme } from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useRef, useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { z } from 'zod';

const EMPTY_STATE = 'Customer comment not provided';

const formSchema = z.object({ comment: z.string().nullable() });

type FormData = z.infer<typeof formSchema>;

interface Props {
  customer: Pick<Customer, 'key' | 'comment'>;
}

/**
 * Customer comment control with inline display and edit capability.
 * Displays the comment with an edit button that opens a popover form for editing.
 * Supports expanding/collapsing long comments and handles empty states.
 */
export function CustomerCommentControl(props: Props) {
  const theme = useTheme();
  const editor = useDisclosure();
  const [expanded, setExpanded] = useState(false);
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const inputRef = usePopoverAutoFocus(editor.isOpen);
  const queryClient = useQueryClient();

  const { formState, register, reset, handleSubmit } = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: { comment: props.customer.comment ?? '' },
  });

  const updateCustomerMutation = useMutation({
    mutationFn: (parameters: UpdateCustomerParameters) =>
      updateCustomer(props.customer.key, parameters),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['customer'] });
      reset({ comment: data.customer.comment ?? '' });
      editor.close();
    },
  });

  const timeout = useTimeout(reset, theme.transitions.duration.short);

  const { isDirty } = formState;

  const handleClose = () => {
    editor.close();
    timeout.start();
  };

  const handleValidSubmission: SubmitHandler<FormData> = (data) => {
    if (isDirty) {
      const comment = data.comment === '' ? null : data.comment;

      return updateCustomerMutation.mutate({ comment });
    }

    editor.close();
  };

  const handleToggleExpand = () => {
    setExpanded((prev) => !prev);
  };

  const comment = props.customer.comment ?? EMPTY_STATE;
  const color =
    props.customer.comment === null ? 'text.secondary' : 'text.primary';

  const isLongComment = comment.length > 200 || comment.split('\n').length > 4;

  return (
    <>
      <EditablePropertyButton
        ref={buttonRef}
        onClick={editor.open}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          minWidth: '20rem',
          minHeight: '5rem',
          alignItems: 'start',
          justifyContent: 'start',
          whiteSpace: 'pre-wrap',
          textAlign: 'left',
          borderLeft: '2px solid',
          borderColor: 'divider',
          borderRadius: 0,
          position: 'relative',
          '& .edit-icon': {
            position: 'absolute',
            top: '0.4rem',
            right: '0.3rem',
            margin: 0,
          },
        }}
      >
        <Box
          sx={{
            width: '100%',
            position: 'relative',
          }}
        >
          <Typography
            variant="body2"
            textAlign="left"
            marginRight="auto"
            whiteSpace="pre-wrap"
            color={color}
            sx={{
              display: '-webkit-box',
              WebkitLineClamp: expanded ? 'unset' : 4,
              WebkitBoxOrient: 'vertical',
              overflow: expanded ? 'auto' : 'hidden',
              textOverflow: 'ellipsis',
              maxHeight: expanded ? '20rem' : 'unset',
              overflowY: expanded ? 'auto' : 'hidden',
            }}
          >
            {comment}
          </Typography>
          {isLongComment && (
            <Typography
              component="span"
              variant="body2"
              onClick={(e) => {
                e.stopPropagation();
                handleToggleExpand();
              }}
              sx={{
                fontStyle: 'italic',
                color: 'primary.main',
                cursor: 'pointer',
                '&:hover': {
                  textDecoration: 'underline',
                },
              }}
            >
              {expanded ? 'read less' : 'read more'}
            </Typography>
          )}
        </Box>
      </EditablePropertyButton>
      <SupportSnackbar
        isOpen={updateCustomerMutation.isError}
        onClose={updateCustomerMutation.reset}
        message="An error occurred while updating the customer comment."
      />
      <PopoverForm
        verticalOffset={24}
        horizontalOffset={8}
        position="left"
        verticalPosition="top"
        anchorElement={buttonRef.current}
        isOpen={editor.isOpen}
        onCancel={handleClose}
        onSubmit={handleSubmit(handleValidSubmission)}
      >
        <TextField
          autoFocus
          inputRef={inputRef}
          multiline
          rows={3}
          size="small"
          InputProps={register('comment')}
          sx={{ width: '20rem' }}
        />
      </PopoverForm>
    </>
  );
}
